/*
  Homepage-only interaction enhancements.

  This file restores the subtle motion from the approved HTML prototype without
  affecting other templates. The page still works fully without JavaScript;
  these behaviors only enhance desktop/fine-pointer interactions.
*/
(function () {
  var BOUND_ATTRIBUTE = 'data-homepage-motion-bound';
  var INTERACTIVE_TARGET_SELECTOR = 'a, button, input, textarea, select, label, summary';

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  /*
    Shopify Theme Editor can reload individual sections. This helper lets us
    find matching nodes inside either the whole document or a reloaded section.
  */
  function getScopedMatches(scope, selector) {
    var matches = [];

    if (!scope) {
      return matches;
    }

    if (scope.matches && scope.matches(selector)) {
      matches.push(scope);
    }

    if (scope.querySelectorAll) {
      matches = matches.concat(toArray(scope.querySelectorAll(selector)));
    }

    return matches;
  }

  function registerCleanup(element, cleanup) {
    if (!element.__tcghiveHomepageCleanups) {
      element.__tcghiveHomepageCleanups = [];
    }

    element.__tcghiveHomepageCleanups.push(cleanup);
  }

  function cleanupScope(scope) {
    getScopedMatches(scope, '[' + BOUND_ATTRIBUTE + '="true"]').forEach(function (element) {
      var cleanups = element.__tcghiveHomepageCleanups || [];

      cleanups.forEach(function (cleanup) {
        cleanup();
      });

      element.__tcghiveHomepageCleanups = [];
      element.removeAttribute(BOUND_ATTRIBUTE);
    });
  }

  function canUseHeroParallax(heroRoot, heroVisual) {
    if (!window.matchMedia || !heroRoot || !heroVisual) {
      return false;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return false;
    }

    if (!window.matchMedia('(pointer: fine)').matches) {
      return false;
    }

    if (!window.matchMedia('(min-width: 768px)').matches) {
      return false;
    }

    return window.getComputedStyle(heroVisual).display !== 'none';
  }

  function initHeroParallax(scope) {
    getScopedMatches(scope, '[data-home-hero]').forEach(function (heroRoot) {
      var heroVisual;
      var heroCards;
      var observer;
      var heroIsVisible = true;
      var rafId = 0;
      var targetX = 0;
      var targetY = 0;

      if (heroRoot.getAttribute(BOUND_ATTRIBUTE) === 'true') {
        return;
      }

      heroVisual = heroRoot.querySelector('.home-hero__visual');
      heroCards = toArray(heroRoot.querySelectorAll('[data-hero-card]'));

      if (!heroVisual || heroCards.length === 0) {
        return;
      }

      heroRoot.setAttribute(BOUND_ATTRIBUTE, 'true');

      function applyOffsets() {
        var shouldUseMotion = canUseHeroParallax(heroRoot, heroVisual) && heroIsVisible;
        var hasOffset = shouldUseMotion && (Math.abs(targetX) > 0.01 || Math.abs(targetY) > 0.01);

        rafId = 0;

        heroCards.forEach(function (card) {
          var depth = Number(card.getAttribute('data-hero-depth') || '1');
          var nextOffsetX = shouldUseMotion ? targetX * depth : 0;
          var nextOffsetY = shouldUseMotion ? targetY * depth : 0;

          card.style.setProperty('--hero-card-offset-x', nextOffsetX.toFixed(2) + 'px');
          card.style.setProperty('--hero-card-offset-y', nextOffsetY.toFixed(2) + 'px');
          card.classList.toggle('is-parallax-active', hasOffset);
        });
      }

      function scheduleApply() {
        if (rafId) {
          return;
        }

        rafId = window.requestAnimationFrame(applyOffsets);
      }

      function resetHeroStack() {
        targetX = 0;
        targetY = 0;
        scheduleApply();
      }

      function handlePointerMove(event) {
        if (!canUseHeroParallax(heroRoot, heroVisual) || !heroIsVisible) {
          return;
        }

        /*
          The reference interaction reacts to the viewport center rather than
          the card container itself. We keep that feel, but batch DOM writes in
          requestAnimationFrame for production performance.
        */
        targetX = (window.innerWidth / 2 - event.clientX) / 50;
        targetY = (window.innerHeight / 2 - event.clientY) / 50;
        scheduleApply();
      }

      function handlePointerLeave() {
        resetHeroStack();
      }

      heroRoot.addEventListener('pointermove', handlePointerMove, { passive: true });
      heroRoot.addEventListener('pointerleave', handlePointerLeave);

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            heroIsVisible = entry.isIntersecting;

            if (!heroIsVisible) {
              resetHeroStack();
            }
          });
        }, { threshold: 0.1 });

        observer.observe(heroRoot);
      }

      registerCleanup(heroRoot, function () {
        heroRoot.removeEventListener('pointermove', handlePointerMove);
        heroRoot.removeEventListener('pointerleave', handlePointerLeave);

        if (observer) {
          observer.disconnect();
        }

        if (rafId) {
          window.cancelAnimationFrame(rafId);
        }
      });
    });
  }

  function initWhyDragScroll(scope) {
    getScopedMatches(scope, '[data-drag-scroll]').forEach(function (container) {
      var activePointerId = null;
      var startX = 0;
      var startScrollLeft = 0;
      var hasDragged = false;
      var shouldSuppressClick = false;

      if (container.getAttribute(BOUND_ATTRIBUTE) === 'true') {
        return;
      }

      if (!container.closest('.why-section')) {
        return;
      }

      container.setAttribute(BOUND_ATTRIBUTE, 'true');

      function updateDraggableState() {
        var supportsFinePointer = window.matchMedia ? window.matchMedia('(pointer: fine)').matches : true;
        var hasOverflow = container.scrollWidth > container.clientWidth + 1;

        if (supportsFinePointer && hasOverflow) {
          container.classList.add('is-draggable');
        } else {
          container.classList.remove('is-draggable');
          container.classList.remove('is-dragging');
        }
      }

      function endDrag() {
        if (activePointerId !== null && container.hasPointerCapture && container.hasPointerCapture(activePointerId)) {
          container.releasePointerCapture(activePointerId);
        }

        if (hasDragged) {
          /*
            A drag can trigger a synthetic click after pointerup. Keep the
            suppression window open for that immediate click only, then clear it.
          */
          window.setTimeout(function () {
            shouldSuppressClick = false;
          }, 0);
        } else {
          shouldSuppressClick = false;
        }

        activePointerId = null;
        startX = 0;
        startScrollLeft = 0;
        hasDragged = false;
        container.classList.remove('is-dragging');
      }

      function handlePointerDown(event) {
        if (event.button !== 0 || event.pointerType === 'touch') {
          return;
        }

        updateDraggableState();

        if (!container.classList.contains('is-draggable')) {
          return;
        }

        if (event.target.closest(INTERACTIVE_TARGET_SELECTOR)) {
          return;
        }

        activePointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = container.scrollLeft;
        hasDragged = false;

        container.setPointerCapture(event.pointerId);
      }

      function handlePointerMove(event) {
        var deltaX;

        if (activePointerId === null || event.pointerId !== activePointerId) {
          return;
        }

        deltaX = event.clientX - startX;

        if (!hasDragged && Math.abs(deltaX) < 4) {
          return;
        }

        hasDragged = true;
        container.classList.add('is-dragging');
        shouldSuppressClick = true;
        event.preventDefault();
        container.scrollLeft = startScrollLeft - deltaX * 2;
      }

      function handleClick(event) {
        if (!shouldSuppressClick) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        shouldSuppressClick = false;
      }

      updateDraggableState();

      container.addEventListener('pointerdown', handlePointerDown);
      container.addEventListener('pointermove', handlePointerMove);
      container.addEventListener('pointerup', endDrag);
      container.addEventListener('pointercancel', endDrag);
      container.addEventListener('lostpointercapture', endDrag);
      container.addEventListener('pointerenter', updateDraggableState, { passive: true });
      container.addEventListener('click', handleClick, true);

      registerCleanup(container, function () {
        container.removeEventListener('pointerdown', handlePointerDown);
        container.removeEventListener('pointermove', handlePointerMove);
        container.removeEventListener('pointerup', endDrag);
        container.removeEventListener('pointercancel', endDrag);
        container.removeEventListener('lostpointercapture', endDrag);
        container.removeEventListener('pointerenter', updateDraggableState);
        container.removeEventListener('click', handleClick, true);
      });
    });
  }

  function formatTrustCount(value, locale) {
    var roundedValue = Math.max(0, Math.round(value));

    if (window.Intl && window.Intl.NumberFormat) {
      return new window.Intl.NumberFormat(locale || document.documentElement.lang || 'de').format(roundedValue) + '+';
    }

    /*
      Older browsers still get readable thousand separators. This fallback is
      only used if Intl is missing, so it deliberately keeps the logic simple.
    */
    return String(roundedValue).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + '+';
  }

  function easeOutCubic(progress) {
    var remaining = 1 - progress;

    return 1 - remaining * remaining * remaining;
  }

  function initTrustCount(scope) {
    getScopedMatches(scope, '[data-trust-count]').forEach(function (countElement) {
      var target = Number(countElement.getAttribute('data-trust-count-target') || '0');
      var finalText = countElement.getAttribute('data-trust-count-final') || countElement.textContent;
      var locale = countElement.getAttribute('data-trust-count-locale') || document.documentElement.lang || 'de';
      var duration = 1400;
      var observer = null;
      var rafId = 0;
      var hasAnimated = false;

      if (countElement.getAttribute(BOUND_ATTRIBUTE) === 'true') {
        return;
      }

      if (!target || target < 1) {
        return;
      }

      countElement.setAttribute(BOUND_ATTRIBUTE, 'true');

      function setFinalValue() {
        countElement.textContent = finalText;
      }

      function animateCount() {
        var startTime = 0;

        if (hasAnimated) {
          return;
        }

        hasAnimated = true;
        countElement.textContent = formatTrustCount(0, locale);

        function step(timestamp) {
          var elapsed;
          var progress;
          var nextValue;

          if (!startTime) {
            startTime = timestamp;
          }

          elapsed = timestamp - startTime;
          progress = Math.min(elapsed / duration, 1);
          nextValue = target * easeOutCubic(progress);

          countElement.textContent = progress >= 1 ? finalText : formatTrustCount(nextValue, locale);

          if (progress < 1) {
            rafId = window.requestAnimationFrame(step);
          } else {
            rafId = 0;
          }
        }

        rafId = window.requestAnimationFrame(step);
      }

      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setFinalValue();
        return;
      }

      if (!window.requestAnimationFrame) {
        setFinalValue();
        return;
      }

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting && entry.intersectionRatio < 0.25) {
              return;
            }

            if (observer) {
              observer.unobserve(countElement);
              observer.disconnect();
              observer = null;
            }

            animateCount();
          });
        }, { threshold: 0.35 });

        observer.observe(countElement);
      } else {
        animateCount();
      }

      registerCleanup(countElement, function () {
        if (observer) {
          observer.disconnect();
        }

        if (rafId) {
          window.cancelAnimationFrame(rafId);
        }
      });
    });
  }

  function initHomepageMotion(scope) {
    initTrustCount(scope);
    initHeroParallax(scope);
    initWhyDragScroll(scope);
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  onReady(function () {
    initHomepageMotion(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    initHomepageMotion(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    cleanupScope(event.target);
  });
})();
